import { useEffect, useState, type RefObject } from "react";

/**
 * True while the element crosses the middle of the viewport, false once it
 * has scrolled away above or below, so a reveal opens on arrival and closes
 * again on departure. Always true without IntersectionObserver and for
 * visitors who prefer reduced motion, who get the revealed state and no
 * movement.
 *
 * @param ref the element to watch, typically a section.
 */
export function useRevealAtCenter(ref: RefObject<HTMLElement | null>): boolean {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return undefined;
    }
    // A thin band across the middle of the viewport (2% tall, so a fast
    // scroll still lands a frame inside it).
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setRevealed(entry.isIntersecting);
      },
      { rootMargin: "-49% 0px -49% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return revealed;
}
